import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Card, Button, Alert, Row, Col } from 'react-bootstrap';
import { RiArrowLeftLine } from 'react-icons/ri';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import PlayerForm from '../../../components/PlayerForm';
import { DeleteModal } from '../../../Common/DeleteModal';


import './PlayerInformation.css';

// Import player management services
import { 
  getPlayerById,
  updatePlayer, 
  deletePlayer,
  Player
} from '../../../api/club/playerService';
import { fetchTeamsByClubId } from '../../../api/club/teamService';
import { getUserClubIds } from '../../../utils/roleBasedAccess';
import { getImageUrl } from '../../../utils/imageUrl';
import PlayerAssetPreviewModal, { PlayerAssetType } from '../../../components/PlayerAssetPreviewModal';
import { PLAYER_ASSETS_ENABLED } from '../../../constants/featureFlags';
import { downloadFileFromUrl } from '../../../utils/fileDownload';



interface Team {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  ageCategory: string;
  coachManagerName: string;
  contactDetails: string;
}

const PlayerDetail: React.FC = () => {
  const navigate = useNavigate();
  const { playerId } = useParams<{ playerId: string }>();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get('edit') === 'true';
  const teamId = searchParams.get('teamId') || '';
  
  // State management
  const [player, setPlayer] = useState<Player | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(teamId);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [teamsError, setTeamsError] = useState<string | null>(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetPreviewType, setAssetPreviewType] = useState<PlayerAssetType | null>(null);

  // Header styling (matching tournament design)
  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
    padding: '0 15px',
    minHeight: '60px',
    flexWrap: 'wrap' as const,
    position: 'relative' as const
  };

  const logoStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    overflow: 'hidden',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #e9ecef',
    flexShrink: 0
  } as const;

  const titleSectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    flexWrap: 'wrap' as const,
    minWidth: '200px',
    marginLeft: '8px'
  } as const;

  useEffect(() => {
    loadPlayerData();
  }, [playerId]);

  // Load player data
  const loadPlayerData = async () => {
    try {
      setLoading(true);
      
      // Load teams
      await loadTeams();

      if (playerId) {
        try {
          // Load specific player data using the new API
          const apiPlayer = await getPlayerById(playerId);
          console.log('Player data loaded:', apiPlayer);
          
          if (apiPlayer) {
            setPlayer(apiPlayer);
          } else {
            setSubmitError('Player not found');
          }
        } catch (error) {
          console.error('Failed to load player:', error);
          setSubmitError('Failed to load player data. Please check the URL and try again.');
        }
      }
    } catch (error) {
      console.error('Failed to load player data:', error);
      setSubmitError('Failed to load player data');
    } finally {
      setLoading(false);
    }
  };

  const loadTeams = async () => {
    try {
      setTeamsError(null);
      
      // Get user's club IDs
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      
      if (clubIds.length === 0) {
        setTeamsError('No club access found for this user. Please contact your administrator.');
        return;
      }
      
      // Use the first club ID (assuming single club access for now)
      const clubId = clubIds[0];
      
      // Fetch teams from API with dynamic club ID
      const teamsData = await fetchTeamsByClubId(clubId);
      const transformedTeams: Team[] = teamsData.map((team: any) => ({
        _id: team._id,
        name: team.name,
        shortName: team.shortName,
        logo: team.logo,
        ageCategory: team.ageCategory || '',
        coachManagerName: '',
        contactDetails: ''
      }));
      setTeams(transformedTeams);
    } catch (error: any) {
      console.error('Failed to load teams:', error);
      setTeamsError(error.message || 'Failed to load teams. Please try again.');
    }
  };



  const handleSavePlayer = async (playerData: any) => {
    if (!player) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const updateData = {
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: playerData.ageCategory,
        parentName: playerData.parentName,
        documents: playerData.documents
      };

      const response = await updatePlayer(player._id, updateData);
      if (response.success) {
        // Navigate back to player information page
        navigate('/my-club/player-information');
      }
    } catch (error: any) {
      console.error('Failed to update player:', error);
      setSubmitError(error.message || 'Failed to update player. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePlayer = async () => {
    setShowDeleteModal(true);
  };

  const getAssetUrl = (type: PlayerAssetType): string => {
    if (!player) return '';
    const assetPath = type === 'qrCode' ? player.qrCode : player.playerIdCard;
    return assetPath ? getImageUrl(assetPath) : '';
  };

  const handleAssetView = (type: PlayerAssetType) => {
    setAssetPreviewType(type);
  };

  const handleAssetDownload = (type: PlayerAssetType) => {
    if (!player) return;
    const assetUrl = getAssetUrl(type);
    if (!assetUrl) return;

    const formattedName = player.playerId || player.name.replace(/\s+/g, '-').toLowerCase();

    const filename =
      type === 'qrCode'
        ? `${formattedName || 'player'}-qr-code.png`
        : `${formattedName || 'player'}-id-card.pdf`;

    downloadFileFromUrl(assetUrl, filename);
  };

  const confirmDeletePlayer = async () => {
    if (!player) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      console.log('🗑️ Starting player deletion for:', player._id);

      await deletePlayer(player._id);
      console.log('🗑️ Delete player successful');
      
      // Always close modal and navigate if no exception was thrown
      // The API response shows success, so we should proceed
      
      // Close modal first
      setShowDeleteModal(false);
      
      // Navigate back to player information page
      navigate('/my-club/player-information');
      
      
      // Success - if we reach here, the deletion was successful
      
    } catch (error: any) {
      console.error('❌ Failed to delete player:', error);
      setSubmitError(error.message || 'Failed to delete player. Please try again.');
    } finally {
      setIsSubmitting(false);
      // Ensure modal is closed even if there's an error
      setShowDeleteModal(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleGoBack = () => {
    navigate('/my-club/player-information');
  };

  const handleCancel = () => {
    navigate('/my-club/player-information');
  };

  const handleTeamChange = (newTeamId: string) => {
    setSelectedTeamId(newTeamId);
  };

  if (loading) {
    return (
      <div className="player-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div className="loading-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="player-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <Alert variant="danger">
            {submitError || 'Player not found'}
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="player-detail-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div style={{ padding: '0 15px', marginBottom: '8px' }}>
          <Breadcrumbs 
            items={[
              { label: 'My Club', onClick: () => navigate('/my-club') },
              { label: 'Player Information', onClick: () => navigate('/my-club/player-information') },
              { label: player.name, active: true }
            ]} 
          />
        </div>

        {/* Player Header - Matching tournament design */}
        <div style={headerStyle}>
          <button 
            className="back-button" 
            onClick={handleGoBack}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6c757d',
              transition: 'background-color 0.2s ease',
              marginRight: '4px',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <RiArrowLeftLine size={20} />
          </button>
          <div style={logoStyle}>
            {player.profilePic ? (
              <img 
                src={getImageUrl(player.profilePic)}
                alt="Player Profile"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('d-none');
                }}
              />
            ) : null}
            {!player.profilePic && (
              <div className="default-profile-icon-detail">
                <i className="ri-user-line"></i>
              </div>
            )}
          </div>
          <div style={titleSectionStyle}>
            <h1 className="player-title" style={{ 
              margin: 0, 
              fontSize: '24px', 
              fontWeight: '600', 
              color: '#212529',
              lineHeight: '1.2'
            }}>
              {player.name}
            </h1>
            <span style={{
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '16px',
              padding: '6px 12px',
              borderRadius: '20px',
              border: 'none',
              display: 'inline-block',
              minWidth: '80px',
              textAlign: 'center',
              ...(player.verificationStatus === 'approved' ? {
                background: '#d4edda',
                color: '#155724'
              } : player.verificationStatus === 'referred_back' ? {
                background: '#fff3cd',
                color: '#856404'
              } : {
                background: '#fff3cd',
                color: '#856404'
              })
            }}>
              {player.verificationStatus.charAt(0).toUpperCase() + player.verificationStatus.slice(1)}
            </span>
          </div>
          
          {/* Action Buttons */}
          <div className="d-flex gap-2">
            {!isEditMode && player && player.verificationStatus !== 'approved' && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(`/my-club/player-information/${playerId}?teamId=${teamId}&edit=true`)}
                style={{
                  background: '#006EFF',
                  border: 'none',
                  color: '#fff',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  minWidth: '80px'
                }}
              >
                <i className="ri-edit-line me-1"></i>
                Edit
              </Button>
            )}
            {player.verificationStatus !== 'approved' && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeletePlayer}
                style={{
                  background: '#dc3545',
                  border: 'none',
                  color: '#fff',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  minWidth: '80px'
                }}
              >
                <i className="ri-delete-bin-line me-1"></i>
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Player Form Card - Using the same card as Add Player */}
        <Card className="mb-4" style={{ margin: '0 15px' }}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">{isEditMode ? 'Edit Player' : 'Player Details'}</h5>
              <Button 
                variant="link" 
                className="close-form-btn"
                onClick={handleCancel}
              >
                <i className="ri-close-line"></i>
              </Button>
            </div>
            
            {teamsError && (
              <div className="alert alert-danger mb-3" role="alert">
                {teamsError}
              </div>
            )}
            
            <PlayerForm
              player={player}
              teamId={selectedTeamId}
              teamName={teams.find(t => t._id === selectedTeamId)?.name || 'Select Team'}
              ageCategories={[]} // Not needed anymore since age category comes from team
              teams={teams.map(team => ({ 
                value: team._id, 
                label: team.ageCategory 
                  ? `${team.name} - ${team.ageCategory}` 
                  : team.name,
                ageCategory: team.ageCategory
              }))}
              showTeamField={true} // Show team field in Player Information
              onTeamChange={undefined} // Disable team changes since API doesn't support it
              // No tournamentId prop - will use dynamic getCurrentActiveTournament()
              onSubmit={handleSavePlayer}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              submitError={submitError}
              isReadOnly={!isEditMode || (player && player.verificationStatus === 'approved')}
              teamAgeCategories={(() => {
                // Get selected team's age category
                const selectedTeam = teams.find(team => team._id === selectedTeamId);
                if (selectedTeam && selectedTeam.ageCategory) {
                  return [selectedTeam.ageCategory];
                }
                return [];
              })()}
              hideAgeCategoryField={true} // Always hide age category field in edit mode since it comes from team
            />
            {/* Debug logging */}
            {player && (
              <div style={{ display: 'none' }}>
                Debug: Player passed to PlayerForm - Documents: {JSON.stringify(player.documents)}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Player Assets */}
        {PLAYER_ASSETS_ENABLED && (
          <Card className="mb-4" style={{ margin: '0 15px' }}>
            <Card.Body>
              <h5 className="card-title mb-4">Player Assets</h5>
              <Row className="g-3">
                <Col md={6}>
                  <div className="asset-card">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="mb-1">QR Code</h6>
                        <p className="text-muted mb-0">Scan to verify player details instantly.</p>
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-dark"
                        onClick={() => handleAssetView('qrCode')}
                        disabled={!player.qrCode}
                      >
                        View
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => handleAssetDownload('qrCode')}
                        disabled={!player.qrCode}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="asset-card">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="mb-1">Player ID Card</h6>
                        <p className="text-muted mb-0">Share or print this ID for match-day checks.</p>
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-dark"
                        onClick={() => handleAssetView('playerIdCard')}
                        disabled={!player.playerIdCard}
                      >
                        View
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => handleAssetDownload('playerIdCard')}
                        disabled={!player.playerIdCard}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Container>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDeletePlayer}
        title="Delete Player"
        message={`Are you sure you want to delete "${player.name}"? This action cannot be undone.`}
        confirmLabel={isSubmitting ? "Deleting..." : "Yes, Delete"}
        cancelLabel="Cancel"
        isLoading={isSubmitting}
        error={submitError}
      />
      {PLAYER_ASSETS_ENABLED && (
        <PlayerAssetPreviewModal
          show={assetPreviewType !== null}
          assetType={assetPreviewType || 'qrCode'}
          assetUrl={assetPreviewType ? getAssetUrl(assetPreviewType) : undefined}
          playerName={player.name}
          onClose={() => setAssetPreviewType(null)}
          onDownload={() => {
            if (!assetPreviewType) return;
            handleAssetDownload(assetPreviewType);
          }}
        />
      )}
    </div>
  );
};

export default PlayerDetail; 